import { useCart } from '@shared/cart-react';
import { productPages } from '@website/data/product-pages';
import analytics from '@website/utils/analytics';
import { formatItemDataEvent, trackEvent } from '@website/utils/ga4';
import { useEffect, useMemo, useState } from 'react';
import useAppLovin, { productAxonCategoryMap } from './useAppLovin';
import useEmotive from './useEmotive';

const useTrackViewProduct = ({ sku }: { sku: string }) => {
  const [axonReady, setAxonReady] = useState(false);
  const [trackingFired, setTrackingFired] = useState(false);
  const { edgetag } = useEmotive();
  const { axon } = useAppLovin();
  const cart = useCart();
  const { cartProducts, products } = cart;
  const product = useMemo(() => {
    if (!products?.[sku]) return null;
    return {
      ...cartProducts[sku],
      ...products[sku],
    };
  }, [products, cartProducts, sku]);

  useEffect(() => {
    let intervalId = setInterval(() => {
      if (typeof window !== 'undefined' && window.axon?.ts) {
        setAxonReady(true);
        clearInterval(intervalId);
      }
    }, 100);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if (product && !trackingFired && axonReady) {
      setTrackingFired(true);
      const analyticsData = formatItemDataEvent(product);
      trackEvent('view_item', analyticsData);

      analytics.track('productPage', {
        ecommerce: {
          product: sku,
        },
      });

      // Emotive Edgetag Tracking
      edgetag.trackViewContent({
        currency: 'USD',
        value: product.salePrice,
        contents: [
          {
            id: product.sku,
            item_price: product.salePrice,
            title: product.name,
            image: product.primaryImage?.url,
            url: window.location.origin + window.location.pathname,
          },
        ],
      });

      // AppLovin Axon Tracking
      window.axon?.('track', 'view_item', {
        currency: 'USD',
        items: [
          {
            item_id: product.sku,
            price: (product?.qty ?? 0) * product.salePrice,
            item_name: product.name,
            item_category_id: productAxonCategoryMap[sku],
            image_url: product.primaryImage?.url,
            quantity: product?.qty ?? 0,
          },
        ],
        value: product.salePrice,
      });

      const item = {
        Name: product.name,
        ProductID: product.pandoId,
        ImageURL: product.primaryImage?.url ?? '',
        URL: productPages[sku as keyof typeof productPages],
        Brand: 'Covesmart',
        Price: product.salePrice,
      };
      window.geq?.event?.('Viewed Product Reclaim', item);
    }
  }, [product, axon, edgetag, sku, trackingFired, axonReady]);
};

export default useTrackViewProduct;
